import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AttendanceService } from '../../services/attendance.service';
import { EmployeeResponseDto, SalaryPaymentResponseDto, SalaryCalculationDto } from '../../interfaces/interfaces';
import { SalaryPaymentDialogComponent } from './salary-payment-dialog.component';

@Component({
  selector: 'app-salary-management',
  templateUrl: './salary-management.component.html',
  styleUrls: ['./salary-management.component.scss']
})
export class SalaryManagementComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'employeeName', 'year', 'month', 'amount', 'hoursWorked', 'daysWorked', 'status', 'paymentDate'
  ];
  dataSource = new MatTableDataSource<SalaryPaymentResponseDto>();
  
  filterForm: FormGroup;
  loading = false;
  employees: EmployeeResponseDto[] = [];
  selectedEmployee: EmployeeResponseDto | null = null;
  salaryCalculation: SalaryCalculationDto | null = null;
  salaryCalculations: SalaryCalculationDto[] = [];
  salarySummaries: { employeeName: string, employeeId: number, totalAmount: number, hoursWorked: number, daysWorked: number, salaryType: string, period: string }[] = [];

  paymentStatuses = [
    { value: 1, label: 'Pending' },
    { value: 2, label: 'Paid' },
    { value: 3, label: 'Overdue' }
  ];

  months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  historyForm: FormGroup;
  salaryPayments: SalaryPaymentResponseDto[] = [];
  salaryHistoryColumns: string[] = [
    'employeeName', 'year', 'month', 'amount', 'hoursWorked', 'daysWorked', 'status', 'paymentDate', 'paymentMethod', 'referenceNumber', 'notes'
  ];
  historySearched = false;

  constructor(
    private attendanceService: AttendanceService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.filterForm = this.fb.group({
      employeeId: [''],
      year: [new Date().getFullYear()],
      month: [new Date().getMonth() + 1],
      status: ['']
    });
    this.historyForm = this.fb.group({
      year: [new Date().getFullYear()],
      month: [new Date().getMonth() + 1],
      status: ['']
    });
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.loadSalaryPayments();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadEmployees(): void {
    this.attendanceService.getAllEmployees(true).subscribe({
      next: (response) => {
        this.employees = response.employees;
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
      }
    });
  }

  loadSalaryPayments(): void {
    this.loading = true;
    const formValue = this.filterForm.value;

    this.attendanceService.getSalaryPayments(
      formValue.employeeId || undefined,
      formValue.year,
      formValue.month,
      formValue.status || undefined
    ).subscribe({
      next: (payments) => {
        this.dataSource.data = payments;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading salary payments:', error);
        this.snackBar.open('Error loading salary payments', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  calculateSalary(): void {
    const formValue = this.filterForm.value;
    this.salaryCalculation = null;
    this.salaryCalculations = [];
    this.selectedEmployee = null;
    this.loading = true;

    if (!formValue.employeeId) {
      // All Employees
      const year = formValue.year;
      const month = formValue.month;
      const requests = this.employees.map(emp =>
        this.attendanceService.calculateSalary(emp.id, year, month)
          .toPromise()
          .then(calc => ({ ...calc, employeeName: emp.fullName }))
          .catch(() => null)
      );

      Promise.all(requests).then(results => {
        this.salaryCalculations = results.filter(r => r !== null);
        this.loading = false;
        if (this.salaryCalculations.length === 0) {
          this.snackBar.open('No salary data found for any employee', 'Close', { duration: 3000 });
        }
        this.updateSalarySummaries();
      });
    } else {
      // Specific Employee
      this.attendanceService.calculateSalary(formValue.employeeId, formValue.year, formValue.month).subscribe({
        next: (calculation) => {
          this.salaryCalculation = calculation;
          this.selectedEmployee = this.employees.find(e => e.id === formValue.employeeId) || null;
          this.salaryCalculations = [calculation];
          this.loading = false;
          this.snackBar.open('Salary calculated successfully', 'Close', { duration: 3000 });
          // Update salarySummaries for single employee
          const emp = this.selectedEmployee;
          this.salarySummaries = [
            {
              employeeName: emp ? emp.fullName : '',
              employeeId: calculation.employeeId,
              totalAmount: calculation.amount,
              hoursWorked: calculation.hoursWorked,
              daysWorked: calculation.daysWorked,
              salaryType: this.getSalaryTypeLabel(calculation.salaryType),
              period: this.getMonthLabel(calculation.month)
            }
          ];
        },
        error: (error) => {
          this.snackBar.open('Error calculating salary', 'Close', { duration: 3000 });
          this.loading = false;
        }
      });
    }
  }

  updateSalarySummaries() {
    this.salarySummaries = this.salaryCalculations.map(result => {
      // Find employee name from employees array
      const emp = this.employees.find(e => e.id === result.employeeId);
      return {
        employeeName: emp ? emp.fullName : '',
        employeeId: result.employeeId,
        totalAmount: result.amount,
        hoursWorked: result.hoursWorked,
        daysWorked: result.daysWorked,
        salaryType: this.getSalaryTypeLabel(result.salaryType),
        period: this.getMonthLabel(result.month)
      };
    });
  }

  getSalaryTypeLabel(type: number): string {
    switch (type) {
      case 1: return 'Hourly';
      case 2: return 'Monthly';
      default: return type.toString();
    }
  }

  getStatusLabel(status: number): string {
    return this.paymentStatuses.find(s => s.value === status)?.label || 'Unknown';
  }

  getStatusColor(status: number): string {
    switch (status) {
      case 1: return 'warn'; // Pending
      case 2: return 'accent'; // Paid
      case 3: return 'primary'; // Overdue
      default: return 'primary';
    }
  }

  getMonthLabel(month: number): string {
    return this.months.find(m => m.value === month)?.label || 'Unknown';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  }

  getTotalAmount(): number {
    return this.dataSource.data.reduce((total, payment) => total + payment.amount, 0);
  }

  getTotalHours(): number {
    return this.dataSource.data.reduce((total, payment) => total + payment.hoursWorked, 0);
  }

  getTotalDays(): number {
    return this.dataSource.data.reduce((total, payment) => total + payment.daysWorked, 0);
  }

  getPaidPayments(): number {
    return this.dataSource.data.filter(payment => payment.status === 2).length;
  }

  getPendingPayments(): number {
    return this.dataSource.data.filter(payment => payment.status === 1).length;
  }

  getOverduePayments(): number {
    return this.dataSource.data.filter(payment => payment.status === 3).length;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onFilterChange(): void {
    this.loadSalaryPayments();
  }

  exportToCSV(): void {
    const data = this.dataSource.data;
    if (data.length === 0) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }

    const headers = ['Employee Name', 'Year', 'Month', 'Amount', 'Hours Worked', 'Days Worked', 'Status', 'Payment Date'];
    const csvContent = [
      headers.join(','),
      ...data.map(row => [
        row.employeeName,
        row.year,
        this.getMonthLabel(row.month),
        this.formatCurrency(row.amount),
        row.hoursWorked.toFixed(2),
        row.daysWorked,
        this.getStatusLabel(row.status),
        this.formatDate(row.paymentDate || '')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Format current date for filename using local date
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    link.download = `salary_report_${dateString}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    this.snackBar.open('Salary report exported successfully', 'Close', { duration: 3000 });
  }

  openSalaryPaymentDialog(summary: any) {
    const dialogRef = this.dialog.open(SalaryPaymentDialogComponent, {
      width: '500px',
      data: {
        ...summary,
        employeeName: summary.employeeName,
        year: this.filterForm.get('year')?.value,
        month: this.filterForm.get('month')?.value,
        period: summary.period
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Salary payment saved successfully', 'Close', { duration: 3000 });
        // Optionally refresh payments list here
      }
    });
  }

  getSalaryPayments() {
    const { year, month, status } = this.historyForm.value;
    this.attendanceService.getSalaryPayments(undefined, year, month, status).subscribe({
      next: (payments) => {
        this.salaryPayments = payments;
        this.historySearched = true;
      },
      error: () => {
        this.salaryPayments = [];
        this.historySearched = true;
        this.snackBar.open('Failed to fetch salary payments', 'Close', { duration: 3000 });
      }
    });
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1: return 'pending';
      case 2: return 'paid';
      case 3: return 'overdue';
      default: return '';
    }
  }

  deleteSalaryPayment(row: SalaryPaymentResponseDto) {
    if (!confirm('Are you sure you want to delete this salary payment?')) return;
    this.attendanceService.deleteSalaryPayment(row.id).subscribe({
      next: () => {
        this.snackBar.open('Salary payment deleted', 'Close', { duration: 3000 });
        this.getSalaryPayments();
      },
      error: () => {
        this.snackBar.open('Failed to delete salary payment', 'Close', { duration: 3000 });
      }
    });
  }
} 